import React, { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import useOtpEntries from '../hooks/useOtpEntries';
import OtpAddModal from '../components/OtpAddModal';
import { Input, PillButton, EmptyState, Toast, FeatureBanner } from '../ui';
import { ReactComponent as AlertTriangleIcon } from '../assets/icons/alert-triangle.svg';
import { ReactComponent as SearchIcon } from '../assets/icons/search.svg';
import { ReactComponent as PlusIcon } from '../assets/icons/plus.svg';
import { ReactComponent as CopyIcon } from '../assets/icons/copy.svg';
import { ReactComponent as TrashIcon } from '../assets/icons/trash.svg';
import { ReactComponent as ShieldIcon } from '../assets/icons/shield.svg';
import './TwoFactor.css';

// Ámbar los últimos 5s de la ventana de 30s: mismo umbral que llevaba OTPManager.js.
const AMBER_THRESHOLD = 5;

function initialsFor(entry) {
  const source = entry.issuer || entry.account || '?';
  return source.trim().slice(0, 2).toUpperCase();
}

function CountdownRing({ timeLeft, amber }) {
  const pct = timeLeft / 30;
  const radius = 15;
  const circumference = 2 * Math.PI * radius;
  return (
    <svg width="36" height="36" viewBox="0 0 36 36" className={`sgc-2fa__ring ${amber ? 'sgc-2fa__ring--amber' : ''}`}>
      <circle cx="18" cy="18" r={radius} className="sgc-2fa__ring-track" />
      <circle
        cx="18" cy="18" r={radius}
        className="sgc-2fa__ring-fill"
        strokeDasharray={circumference}
        strokeDashoffset={circumference * (1 - pct)}
      />
      <text x="18" y="22" textAnchor="middle" className="sgc-2fa__ring-text">{timeLeft}</text>
    </svg>
  );
}

function TwoFactor() {
  const { t } = useTranslation();
  const { entries, loading, loadError, codes, timeLeft, addEntries, flush, deleteEntry } = useOtpEntries();
  const [search, setSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [toast, setToast] = useState(null);
  const amber = timeLeft <= AMBER_THRESHOLD;

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    if (!q) return entries;
    return entries.filter((e) => (e.issuer || '').toLowerCase().includes(q) || (e.account || '').toLowerCase().includes(q));
  }, [entries, search]);

  const copy = useCallback((entry) => {
    const code = codes[entry.id];
    if (!code) return;
    navigator.clipboard.writeText(code);
    setToast({ type: 'success', message: t('otp.copy') });
  }, [codes, t]);

  const remove = useCallback((entry) => {
    if (!window.confirm(t('otp.confirmDelete'))) return;
    deleteEntry(entry.id);
  }, [deleteEntry, t]);

  return (
    <div className="sgc-2fa">
      <div className="sgc-2fa__header">
        <h1 className="sgc-2fa__title">{t('otp.title')}</h1>
        <PillButton icon={PlusIcon} size="sm" onClick={() => setShowAdd(true)}>{t('otp.addNew')}</PillButton>
      </div>

      <Input
        icon={SearchIcon}
        placeholder={t('otp.searchPlaceholder')}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      {loadError === 'decrypt' && (
        <FeatureBanner icon={AlertTriangleIcon} tone="warn" title={t('otp.undecryptableTitle')} subtitle={t('otp.undecryptableDesc')} />
      )}
      {loadError === 'load' && (
        <FeatureBanner icon={AlertTriangleIcon} tone="warn" title={t('otp.loadFailed')} />
      )}

      {loading ? (
        <p className="sgc-2fa__loading">{t('common.loading')}</p>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={ShieldIcon}
          title={entries.length === 0 ? t('otp.noEntries') : t('common.search')}
          description={entries.length === 0 ? t('otp.noEntriesDesc') : ''}
          actionLabel={entries.length === 0 ? t('otp.addNew') : undefined}
          onAction={entries.length === 0 ? () => setShowAdd(true) : undefined}
        />
      ) : (
        <ul className="sgc-2fa__list">
          {filtered.map((entry) => (
            <li key={entry.id} className="sgc-2fa__row" onClick={() => copy(entry)}>
              <span className="sgc-2fa__avatar">{initialsFor(entry)}</span>
              <div className="sgc-2fa__info">
                <p className="sgc-2fa__issuer">{entry.issuer || t('otp.unknownIssuer')}</p>
                <p className="sgc-2fa__account">{entry.account}</p>
              </div>
              <span className={`sgc-2fa__code ${amber ? 'sgc-2fa__code--amber' : ''}`}>
                {(codes[entry.id] || '------').replace(/(\d{3})(\d{3})/, '$1 $2')}
              </span>
              <CountdownRing timeLeft={timeLeft} amber={amber} />
              <button
                type="button"
                className="sgc-2fa__row-action"
                onClick={(e) => { e.stopPropagation(); copy(entry); }}
                title={t('otp.copy')}
              >
                <CopyIcon width={16} height={16} aria-hidden="true" />
              </button>
              <button
                type="button"
                className="sgc-2fa__row-action"
                onClick={(e) => { e.stopPropagation(); remove(entry); }}
                title={t('otp.delete')}
              >
                <TrashIcon width={16} height={16} aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <OtpAddModal open={showAdd} onClose={() => setShowAdd(false)} entries={entries} addEntries={addEntries} flush={flush} />

      {toast && <div className="sgc-2fa__toast"><Toast type={toast.type} message={toast.message} onClose={() => setToast(null)} /></div>}
    </div>
  );
}

export default TwoFactor;
